import type { AlttvisionApi } from './types/api';

export const ALTTVISION_BRIDGE_APP_ID = 'alttvision-desktop';
export const ALTTVISION_BRIDGE_CONTRACT_VERSION = '2026-06-22.2';

export const ALTTVISION_BRIDGE_API_REQUIREMENTS = {
  system: ['getAppInfo', 'getBootStatus', 'openExternal', 'saveTextFile', 'readClipboardText', 'writeClipboardText'],
  dashboard: ['getOverview'],
  search: ['querySearchIndex'],
  undo: ['getLastUndoable', 'undoLast'],
  customers: [
    'listCustomers',
    'getCustomerById',
    'deleteCustomers',
    'listDeletedCustomers',
    'restoreCustomers',
    'purgeCustomers',
    'createCustomer',
    'updateCustomer',
    'applyBulkPatch'
  ],
  leads: [
    'listLeads',
    'getLeadById',
    'createLead',
    'updateLead',
    'applyBulkPatch',
    'planContacts',
    'logLeadActivity',
    'convertLead',
    'previewLeadImport',
    'importLeadsJson',
    'exportLeadsJson',
    'deleteLeads',
    'listDeletedLeads',
    'restoreLeads',
    'purgeLeads'
  ],
  tasks: [
    'listTasks',
    'moveTaskToStatus',
    'reorderTaskWithinStatus',
    'deleteTasks',
    'listDeletedTasks',
    'restoreTasks',
    'purgeTasks',
    'createTask',
    'updateTask',
    'applyBulkPatch'
  ],
  resources: ['listAssets', 'getAssetById', 'createAsset', 'importAssets', 'updateAsset', 'applyBulkPatch', 'deleteAssets'],
  settings: [
    'getAppSettings',
    'updateAppSettings',
    'getWorkspaceSettings',
    'updateWorkspaceSettings',
    'updateWorkspaceLocalEnvironmentSecrets',
    'updateWorkspacePaymentSecrets',
    'updateWorkspaceIntegrationSecrets',
    'testOblioConnection',
    'refreshOblioNomenclature',
    'getWordPressBridgeStatus',
    'refreshWordPressBridgeStatus',
    'getWordPressBridgeContract',
    'pickDirectory',
    'listCompanyProfiles',
    'saveCompanyProfile',
    'listCustomerCategories',
    'saveCustomerCategory'
  ],
  preferences: ['getUserPreferences', 'updateUserPreferences'],
  projects: [
    'listProjects',
    'getProjectById',
    'applyBulkPatch',
    'deleteProjects',
    'listDeletedProjects',
    'restoreProjects',
    'purgeProjects',
    'createProject',
    'updateProject',
    'createBuilderDocument',
    'updateBuilderDocument',
    'deleteBuilderDocument',
    'setPrimaryBuilderDocument',
    'updateWordPressAdminCredentials',
    'attachProjectBrandingAsset',
    'provisionProjectRuntime',
    'updateProjectRuntime',
    'getProjectRuntimeStatus',
    'openProjectLocalUrl'
  ],
  contracts: [
    'listContracts',
    'getContractById',
    'listContractTemplates',
    'getContractTemplateById',
    'saveContractTemplate',
    'previewContractGeneration',
    'generateContractFromTemplate',
    'deleteContracts',
    'listDeletedContracts',
    'restoreContracts',
    'purgeContracts',
    'createContract',
    'updateContract',
    'applyBulkPatch'
  ],
  invoices: [
    'listInvoices',
    'getInvoiceById',
    'deleteInvoices',
    'listDeletedInvoices',
    'restoreInvoices',
    'purgeInvoices',
    'createInvoice',
    'updateInvoice',
    'applyBulkPatch',
    'replaceInvoiceLineItems',
    'syncInvoiceToOblio'
  ],
  payments: [
    'listPaymentOrders',
    'getPaymentOrderById',
    'createPaymentOrder',
    'updatePaymentOrder',
    'syncPaymentOrder',
    'refreshPaymentOrder',
    'deletePaymentOrders',
    'listDeletedPaymentOrders',
    'restorePaymentOrders',
    'purgePaymentOrders'
  ],
  ai: [
    'listModels',
    'getProviderKeyStatus',
    'setProviderKey',
    'testProviderKey',
    'generateBuilderPage',
    'editBuilderSection',
    'clearCache'
  ]
} as const satisfies Record<keyof AlttvisionApi, readonly string[]>;

export type BridgeDiagnosticStatus =
  | 'ready'
  | 'browser'
  | 'preload-missing'
  | 'api-missing'
  | 'api-incomplete'
  | 'api-stale'
  | 'version-mismatch';

export interface AlttvisionBridgeContract {
  appId: typeof ALTTVISION_BRIDGE_APP_ID;
  version: string;
  loadedAt: string;
  requiredApi?: typeof ALTTVISION_BRIDGE_API_REQUIREMENTS;
}

export interface BridgeDiagnostic {
  status: BridgeDiagnosticStatus;
  expectedVersion: string;
  hasApi: boolean;
  hasBridge: boolean;
  isElectron: boolean;
  missingDomains: string[];
  missingMethods: string[];
  actualVersion?: string;
}

interface BridgeEnvironmentInput {
  api: unknown;
  bridge: unknown;
  userAgent?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object');
}

function getBridgeVersion(bridge: unknown): string | undefined {
  if (!isRecord(bridge) || typeof bridge.version !== 'string') {
    return undefined;
  }

  return bridge.version;
}

function getBridgeAppId(bridge: unknown): string | undefined {
  if (!isRecord(bridge) || typeof bridge.appId !== 'string') {
    return undefined;
  }

  return bridge.appId;
}

function getApiShapeIssues(api: unknown): Pick<BridgeDiagnostic, 'missingDomains' | 'missingMethods'> {
  const missingDomains: string[] = [];
  const missingMethods: string[] = [];

  if (!isRecord(api)) {
    return {
      missingDomains: Object.keys(ALTTVISION_BRIDGE_API_REQUIREMENTS),
      missingMethods
    };
  }

  Object.entries(ALTTVISION_BRIDGE_API_REQUIREMENTS).forEach(([domain, methods]) => {
    const domainApi = api[domain];

    if (!isRecord(domainApi)) {
      missingDomains.push(domain);
      return;
    }

    methods.forEach((method) => {
      if (typeof domainApi[method] !== 'function') {
        missingMethods.push(`${domain}.${method}`);
      }
    });
  });

  return { missingDomains, missingMethods };
}

function createDiagnostic(
  status: BridgeDiagnosticStatus,
  input: BridgeEnvironmentInput,
  isElectron: boolean,
  shapeIssues: Pick<BridgeDiagnostic, 'missingDomains' | 'missingMethods'>
): BridgeDiagnostic {
  const actualVersion = getBridgeVersion(input.bridge);
  const diagnostic: BridgeDiagnostic = {
    status,
    expectedVersion: ALTTVISION_BRIDGE_CONTRACT_VERSION,
    hasApi: isRecord(input.api),
    hasBridge: isRecord(input.bridge),
    isElectron,
    missingDomains: shapeIssues.missingDomains,
    missingMethods: shapeIssues.missingMethods
  };

  if (actualVersion) {
    diagnostic.actualVersion = actualVersion;
  }

  return diagnostic;
}

export function createAlttvisionBridgeContract(): AlttvisionBridgeContract {
  return {
    appId: ALTTVISION_BRIDGE_APP_ID,
    version: ALTTVISION_BRIDGE_CONTRACT_VERSION,
    requiredApi: ALTTVISION_BRIDGE_API_REQUIREMENTS,
    loadedAt: new Date().toISOString()
  };
}

export function diagnoseBridgeEnvironment(input: BridgeEnvironmentInput): BridgeDiagnostic {
  const userAgent = input.userAgent ?? '';
  const shapeIssues = getApiShapeIssues(input.api);
  const hasApi = isRecord(input.api);
  const hasBridge = isRecord(input.bridge);
  const isElectron = userAgent.toLowerCase().includes('electron') || hasApi || hasBridge;

  if (!hasApi && !hasBridge) {
    return createDiagnostic(isElectron ? 'preload-missing' : 'browser', input, isElectron, shapeIssues);
  }

  if (!hasBridge) {
    return createDiagnostic('api-stale', input, isElectron, shapeIssues);
  }

  if (getBridgeAppId(input.bridge) !== ALTTVISION_BRIDGE_APP_ID || !getBridgeVersion(input.bridge)) {
    return createDiagnostic('api-stale', input, isElectron, shapeIssues);
  }

  if (getBridgeVersion(input.bridge) !== ALTTVISION_BRIDGE_CONTRACT_VERSION) {
    return createDiagnostic('version-mismatch', input, isElectron, shapeIssues);
  }

  if (!hasApi) {
    return createDiagnostic('api-missing', input, isElectron, shapeIssues);
  }

  if (shapeIssues.missingDomains.length > 0 || shapeIssues.missingMethods.length > 0) {
    return createDiagnostic('api-incomplete', input, isElectron, shapeIssues);
  }

  return createDiagnostic('ready', input, isElectron, shapeIssues);
}
